package dev.lingvo.shared

import kotlinx.serialization.Serializable

@Serializable
enum class StorePlatform(val id: String, val displayName: String) {
    APP_STORE("app_store", "App Store Connect"),
    GOOGLE_PLAY("google_play", "Google Play"),
    ;

    companion object {
        fun fromId(id: String): StorePlatform? = entries.firstOrNull { it.id.equals(id, ignoreCase = true) }
    }
}

/** What a store counts when it enforces a limit. Apple's keyword field is the only byte budget. */
@Serializable
enum class StoreLimitUnit { CHARACTERS, BYTES }

/**
 * One localizable store field and the limit its store enforces.
 *
 * **Limits live here and nowhere else.** The editor, the push worker and the fastlane importer all
 * read this table, so a store changing a limit is one edit. Figures are the published App Store
 * Connect and Google Play Console limits; [required] fields are the ones a locale cannot go live
 * without.
 *
 * [keyName] is the key the field is stored under inside a project's `store/<platform>` file, which is
 * how a listing reuses the existing translation, glossary and review pipeline instead of forking it.
 */
@Serializable
enum class StoreField(
    val platform: StorePlatform,
    val keyName: String,
    val limit: Int,
    val unit: StoreLimitUnit,
    val required: Boolean,
    /** Output of a machine draft is a draft until a person has looked at it. Never auto-pushed. */
    val reviewOnly: Boolean = false,
) {
    APP_NAME(StorePlatform.APP_STORE, "app_name", 30, StoreLimitUnit.CHARACTERS, required = true),
    APP_SUBTITLE(StorePlatform.APP_STORE, "subtitle", 30, StoreLimitUnit.CHARACTERS, required = false),
    APP_DESCRIPTION(StorePlatform.APP_STORE, "description", 4000, StoreLimitUnit.CHARACTERS, required = true),
    APP_PROMOTIONAL_TEXT(StorePlatform.APP_STORE, "promotional_text", 170, StoreLimitUnit.CHARACTERS, required = false),
    APP_KEYWORDS(StorePlatform.APP_STORE, "keywords", 100, StoreLimitUnit.BYTES, required = true, reviewOnly = true),
    APP_SUPPORT_URL(StorePlatform.APP_STORE, "support_url", 2000, StoreLimitUnit.CHARACTERS, required = true),
    APP_WHATS_NEW(StorePlatform.APP_STORE, "whats_new", 4000, StoreLimitUnit.CHARACTERS, required = false),

    PLAY_TITLE(StorePlatform.GOOGLE_PLAY, "title", 30, StoreLimitUnit.CHARACTERS, required = true),
    PLAY_SHORT_DESCRIPTION(StorePlatform.GOOGLE_PLAY, "short_description", 80, StoreLimitUnit.CHARACTERS, required = true),
    PLAY_FULL_DESCRIPTION(StorePlatform.GOOGLE_PLAY, "full_description", 4000, StoreLimitUnit.CHARACTERS, required = true),
    PLAY_RELEASE_NOTES(StorePlatform.GOOGLE_PLAY, "release_notes", 500, StoreLimitUnit.CHARACTERS, required = false),
    ;

    companion object {
        fun forPlatform(platform: StorePlatform): List<StoreField> = entries.filter { it.platform == platform }

        fun byKey(platform: StorePlatform, keyName: String): StoreField? =
            entries.firstOrNull { it.platform == platform && it.keyName == keyName }

        /** The project file path a platform's listing lives under. */
        fun filePath(platform: StorePlatform): String = "store/${platform.id}"
    }
}

/** Why a store value cannot be pushed. Distinct from QA warnings: these are refusals, not advice. */
@Serializable
enum class StoreIssueKind { OVER_LIMIT, MISSING_REQUIRED, DUPLICATE_KEYWORD, EMPTY_KEYWORD, INVALID_URL }

@Serializable
data class StoreIssue(
    val field: StoreField,
    val kind: StoreIssueKind,
    val message: String,
)

object StoreLimits {
    /** UTF-8 length, computed without `java.nio` so the same rule runs in the browser editor. */
    fun utf8Length(value: String): Int {
        var total = 0
        var index = 0
        while (index < value.length) {
            val char = value[index]
            val code = char.code
            when {
                code < 0x80 -> total += 1
                code < 0x800 -> total += 2
                char.isHighSurrogate() && index + 1 < value.length && value[index + 1].isLowSurrogate() -> {
                    total += 4
                    index++
                }
                else -> total += 3
            }
            index++
        }
        return total
    }

    /** Characters are code points, not UTF-16 units: an emoji is one character to both stores. */
    fun codePointLength(value: String): Int {
        var total = 0
        var index = 0
        while (index < value.length) {
            if (value[index].isHighSurrogate() && index + 1 < value.length && value[index + 1].isLowSurrogate()) {
                index++
            }
            total++
            index++
        }
        return total
    }

    fun measure(field: StoreField, value: String): Int = when (field.unit) {
        StoreLimitUnit.CHARACTERS -> codePointLength(value)
        StoreLimitUnit.BYTES -> utf8Length(value)
    }

    fun remaining(field: StoreField, value: String): Int = field.limit - measure(field, value)

    /** Every reason this one value cannot be pushed. Empty means it can. */
    fun validate(field: StoreField, value: String?): List<StoreIssue> {
        val issues = mutableListOf<StoreIssue>()
        val text = value.orEmpty()
        if (text.isBlank()) {
            if (field.required) {
                issues += StoreIssue(field, StoreIssueKind.MISSING_REQUIRED, "${field.keyName} is required.")
            }
            return issues
        }
        val used = measure(field, text)
        if (used > field.limit) {
            val unit = if (field.unit == StoreLimitUnit.BYTES) "byte" else "character"
            issues += StoreIssue(
                field,
                StoreIssueKind.OVER_LIMIT,
                "${field.keyName} is $used $unit(s); the ${field.platform.displayName} limit is ${field.limit}.",
            )
        }
        if (field == StoreField.APP_SUPPORT_URL && !Regex("https?://[^\\s/?#]+(?:[/?#][^\\s]*)?", RegexOption.IGNORE_CASE).matches(text)) {
            issues += StoreIssue(field, StoreIssueKind.INVALID_URL, "support_url must be an HTTP or HTTPS URL.")
        }
        if (field == StoreField.APP_KEYWORDS) issues += keywordIssues(text)
        return issues
    }

    /** One locale's whole listing: every field's issues, including required fields that are absent. */
    fun validateListing(
        platform: StorePlatform,
        values: Map<StoreField, String?>,
        requiresWhatsNew: Boolean = false,
    ): List<StoreIssue> = StoreField.forPlatform(platform).flatMap { field ->
        if (field == StoreField.APP_WHATS_NEW && requiresWhatsNew && values[field].isNullOrBlank()) {
            listOf(StoreIssue(field, StoreIssueKind.MISSING_REQUIRED, "whats_new is required after the first version."))
        } else {
            validate(field, values[field])
        }
    }

    private fun keywordIssues(value: String): List<StoreIssue> {
        val terms = splitKeywords(value)
        val issues = mutableListOf<StoreIssue>()
        if (terms.any { it.isEmpty() }) {
            issues += StoreIssue(StoreField.APP_KEYWORDS, StoreIssueKind.EMPTY_KEYWORD, "keywords has an empty entry.")
        }
        val seen = mutableSetOf<String>()
        for (term in terms.filter { it.isNotEmpty() }) {
            if (!seen.add(term.lowercase())) {
                issues += StoreIssue(
                    StoreField.APP_KEYWORDS,
                    StoreIssueKind.DUPLICATE_KEYWORD,
                    "keywords repeats '$term'.",
                )
            }
        }
        return issues
    }

    /** Apple's keyword field is comma-separated; spaces around a comma are wasted bytes. */
    fun splitKeywords(value: String): List<String> = value.split(',').map { it.trim() }
}
